import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { Observable, Subscription, BehaviorSubject, Observer, Subject, of } from 'rxjs';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { concatMap, tap, map } from 'rxjs/operators';

// ----------------------------------------------------
/*

    RxJS Subjects are both an Observable and an Observer. Using these Subjects we can concurrently listen to and send messages to a single websocket, this essentially opens up two way communication and allows us to do cool things such as build chat systems.

    The Websocket service will handle direct communication with the socket and the Chat service will expose a simple interface that our other components can easily interact with.

    private defferedPromptSubject = new Subject<any>();
    https://stackoverflow.com/questions/43348463/what-is-the-difference-between-subject-and-behaviorsubject
    https://stackoverflow.com/questions/39494058/behaviorsubject-vs-observable

    // Bring up the score-tracker page directly for testing
    http://localhost:4900/score-tracker/1850?leagueID=1850&matchID=2&gameID=1

    Use behaviorSubject to get messages from the server

*/
// ----------------------------------------------------

@Injectable({
  providedIn: 'root',
})
export class WebSocketService
{
    // Used to Get the Logged In Players ID  
    userTokenInfo;

    // Server URL
    socketApiUrl = environment.socketApiUrl;    

    socketHttpApiUrl = environment.socketHttpApiUrl;

    // API URL
    apiUrl = environment.apiUrl;    

    // Set Local Storage Variable
    localStorage = window.localStorage;

    private socket: any = null;

    connectionInfo = {
        adminID: '',
        playerID: '',
        eventID: 0,
        deviceCourtNumber: -1,
    };
    

    // TODO: use BehaviorSubject ONLY instead of Observable
    // BehaviorSubject internally by the API to call.next(), only service function has ability to call .next()

    // Holds the current Socket Status 
    public socketStatusSubject$ = new BehaviorSubject<any>({});
    // 
    public newScoresDataSubject$ = new BehaviorSubject<any>({});
    // 
    public scoreTrackerBasicScoresSubject$ = new BehaviorSubject<any>({});
    // 
    public savedMatchScoresBasicFromDeviceSubject$ = new Subject<any>();
    // 
    public savedMatchScoresBasicFromAdminSubject$ = new Subject<any>();
    // 
    public onMatchAssignToCourtSubject$ = new BehaviorSubject<any>({eventID: 0});
    // Used to pass the chart data back to the Match Chart for dynamic updating
    public matchChartDataSubject$ = new BehaviorSubject<any>({});

    // When and Admin Device Successfully Navigates to the ScoreTracker Page
    public deviceSucessfullNaviagationSubject$ = new BehaviorSubject<any>({});   

    // When and Admin Device Disconnects let the Admin Device Page know
    public adminDeviceObjSubject$ = new BehaviorSubject<any>({});


    // Holds info if the device is used as courtside tablet
    localUniqueDeviceInfo =  null;    
    webSocketInfo = {
        connectionId: null,
        groupsList: []
    };


    constructor(private http: HttpClient,
                private authService: AuthService,
                private router: Router,
                private snackBar: MatSnackBar,       
    ) 
    {
        this.checkDeviceSetup(); 
    }

    // This is used if the device is refreshed while on the scoreTrackerBasic
    // it will need to connect as an admin device with adminID = NN, eventID = 0 and CourtID
    // from localstorage
    // Check to See if this device is setup as a courtside device
    //
    // ----------------------------------------------------
    // Check to See if the Device has been setup previously
    // Stored in local storage
    // ----------------------------------------------------
    checkDeviceSetup()
    {
        this.localUniqueDeviceInfo = this.getLocalStorageDeviceInfo();   
    }    


    public connectToServer(connectionInfo, joinEventID): Observable<any> 
    {

        // This CHECK might be scoreboard only because of dynamically changing events
        // If the socket is already OPEN and the JoinEventID is -1 means the scoreboard is being used in a different event
        // and needs to join this new eventID
        if(this.socket?.OPEN == 1 && this.webSocketInfo.groupsList.indexOf(`${ connectionInfo.adminID }-${ connectionInfo.joinEventID }`) === -1) 
        {
            // Join the Group tied to the eventID so that scoreboard and leaderboard are updated
            this.joinEventGroup(joinEventID).subscribe((data) => {
                console.log('joinEventGroupd -->> ', joinEventID);
                return of({});
            });            
        }  

        if(this.socket != null && (this.socket?.readyState == this.socket?.OPEN))
        {
            console.log('WebSocket already opened');
            return of({});
        }

        let url = `${ this.socketHttpApiUrl }/negotiate?adminID=${ connectionInfo.adminID }&eventID=${ connectionInfo.eventID }`;

        this.connectionInfo = {...connectionInfo};

        return this.http.get(url).pipe(
            tap((data: any) => 
            {   
                this.configureWebSocketEvents(data, connectionInfo, joinEventID);
            }),
            map((data) => data)
        );   
    }

    configureWebSocketEvents(data, connectionInfo, joinEventID)
    {

        this.socket = new WebSocket(data.url, "json.webpubsub.azure.v1"); 
        // console.log('Socket : ', this.socket);

        this.socket.onmessage = (event) => 
        {
            if (event.data) 
            {
                let message = JSON.parse(event.data);
                console.log(' Before OnMessage Check : ', message.data);

                if(message.type === "message" && message.group === `${ connectionInfo.adminID }-${ connectionInfo.eventID }`)
                {
                    let action = message.data.action;
                    if(action == 'update-score-basic')
                    {
                        // ----------------------------------------
                        // Listen for New Score Data from 
                        // ScoreTrackerBasic - OverAll Score Only
                        // ----------------------------------------             
                        console.log('client side update-score-basic : ', message.data);
                        this.scoreTrackerBasicScoresSubject$.next(message.data.data);
                    }
                    else if(action == 'saved-match-scores-basic-device')
                    {
                        // ----------------------------------------
                        // Listen for Successfully Saved Overall Match Score
                        // ScoreTrackerBasic - OverAll Score Only 
                        // ----------------------------------------                         
                        console.log('client side saved-match-scores-basic-device : ', message.data);
                        this.savedMatchScoresBasicFromDeviceSubject$.next(message.data.data);
                    }          
                    else if(action == 'saved-match-scores-basic-admin')
                    {
                        // ----------------------------------------
                        // The Admin Saved the Score in the Portal
                        // Let the tablet know
                        // ----------------------------------------
                        console.log('client side saved-match-scores-basic-admin : ', message.data);
                        this.savedMatchScoresBasicFromAdminSubject$.next(message.data.data);
                    }                               
                    else if(action == 'update-score-round')
                    {
                        // ----------------------------------------
                        // TODO: SEE WHY this is the same as above
                        // Listen for New Score Data from 
                        // ScoreTrackerROUND or ESPN View - BagTrackerModel
                        // with the InningParm which is an Array that stores
                        // each inning  
                        // ----------------------------------------
                        console.log('client side update-score-round : ', message.data);
                        this.savedMatchScoresBasicFromAdminSubject$.next(message.data.data);
                    }   
                    else if(action == 'match-assigned-to-court')
                    {
                        // ----------------------------------------
                        // Listen for courtNumberAssignments -
                        // the TV Mode will update the InProgress List
                        // ----------------------------------------                          
                        console.log('client side match-assigned-to-court : ', message.data);
                        this.onMatchAssignToCourtSubject$.next(message.data.data);  
                    }       
                    else if(action == 'court-number-assigned-to-device')
                    {
                        // ** USED TO BE onCourtNumberAssigned **
                        // ----------------------------------------
                        // Listen for the Court Number has been assigned Message
                        // The Admin portal assigned a court number
                        // let the tablets know
                        // ----------------------------------------                           
                        console.log('client side court-number-assigned-to-device : ', message.data);
                        console.log(`Match # ${ message.data.data.matchID } has been assigned to ${ message.data.data.courtID }`);

                        let localDeviceInfo = this.getLocalStorageDeviceInfo();

                        if(parseInt(localDeviceInfo.deviceCourtNumber) == message.data.data.courtID)
                        {   
                            if(!message.data.data.hasOwnProperty('gameID'))
                            {
                                message.data.data['gameID'] = 1;
                            }

                            this.router.navigate([`score-tracker/${ message.data.data.leagueID }`],
                            {
                                queryParams: 
                                {   
                                    leagueID:  message.data.data.leagueID,
                                    matchID: message.data.data.matchID,
                                    gameID: message.data.data.gameID,
                                    courtID: message.data.data.courtID
                                }                    
                            });

                        }                         
                    }    
                    else if(action == 'event-info-assigned-to-socket')
                    {
                        // TODO: See if this is even necessary
                        console.log('client side event-info-assigned-to-socket : ', message.data);
                        this.savedMatchScoresBasicFromAdminSubject$.next(message.data.data);
                    }           
                    else if(action == 'navigated-to-score-tracker')
                    {
                        // TODO: See if this is even necessary
                        console.log('client side navigated-to-score-tracker : ', message.data);
                        this.deviceSucessfullNaviagationSubject$.next(message.data.data);
                    }
                    else if(action == 'admin-test')
                    {
                        console.log('client side admin-test : ', message.data.data);
                    }                           
                    else
                    {
                        console.log('Message Recieved to the Group but NO matching Action found!!');
   
                    }                                                                           
                }
                else
                {
                    let webSocketConnectionInfo = JSON.parse(event.data);
                    this.webSocketInfo.connectionId = webSocketConnectionInfo.connectionId;   
                    if (this.webSocketInfo.groupsList.indexOf(`${ connectionInfo.adminID }-${ connectionInfo.eventID }`) === -1) 
                    {
                        this.webSocketInfo.groupsList.push(`${ connectionInfo.adminID }-${ connectionInfo.eventID }`);

                        if(connectionInfo.eventID == 0)
                        {
                            // Add the ScoreZone if its a Admin Tablet
                            // Permissions added server side
                            this.webSocketInfo.groupsList.push(`${ connectionInfo.adminID }-1`);
                        }
                    }   

                    localStorage.setItem(`webSocketInfo`, `${ JSON.stringify(this.webSocketInfo) }`);
                    
                    // Join the Group tied to the eventID so that scoreboard and leaderboard are updated
                    this.joinEventGroup(joinEventID).subscribe((data) => {
                        console.log('joinEventGroupd -->> ');
                        return
                    });
                }
            }
        }; 

        this.socket.onopen = (event) => 
        {
            this.getSocketStatus(); 

            this.socket.send(JSON.stringify({
                type: "joinGroup",
                group: `${ connectionInfo.adminID }-${ connectionInfo.eventID }`
            }));        

            window.onbeforeunload = function() 
            {
              this.localStorage.removeItem('webSocketInfo');
              return '';
            };            
        };

        this.socket.onerror = (event) => 
        {
            console.error("WebSocket error observed:", event);
            if (!navigator.onLine) 
            {
                alert('You are offline. Please connect to the Internet and try again.');
                this.localStorage.removeItem('webSocketInfo');
            }           
        };        

        this.socket.onclose = (event) => 
        {
            this.localStorage.removeItem('webSocketInfo');
            this.getSocketStatus();
            console.log("WebSocket is closed now.", event);
            console.log("Socket hard closed >> readyState: ", this.socket.readyState);
        };        
    }

    // ------------------------------------------------------------
    // Check to see if the Socket has been initialized or connected
    /*
        CONNECTING 0 The connection is not yet open.
        OPEN       1 The connection is open and ready to communicate.
        CLOSING    2 The connection is in the process of closing.
        CLOSED     3 The connection is closed or couldn't be opened.        
    */    
    // ------------------------------------------------------------
    private getSocketStatus()
    {
        this.socketStatusSubject$.next(this.socket);
        return;        
    }


    // ----------------------------------------
    // Trigger Disconnect Client from Server
    // ----------------------------------------
    public disconnectFromServer()
    {
        console.log('this.socket.readyState : ', this.socket.readyState);
        console.log('this.socket.OPEN : ', this.socket.OPEN);        
        if(this.socket != null)
        {
            this.socket.close(1000);            
        }
    }  

    // ----------------------------------------
    // Send Scores From Score Tracker Basic
    //
    // ----------------------------------------
    public emitUpdateScoreBasic(matchScoreObj): Observable<any> 
    {
        console.log('Websocket >> updateScoreBasic : ', matchScoreObj);
        // this.socket.send(JSON.stringify({'action': 'update-score-basic', 'data': matchScoreObj}));
        let adminID = matchScoreObj.adminID;
        let eventID = matchScoreObj.leagueID;

        // Let the Leaderboard Know
        this.socket.send(JSON.stringify({
            type: "sendToGroup",
            noEcho: true,
            group: `${ adminID }-${ eventID }`,
            data: {
                eventName: 'message', 
                action: 'update-score-basic',                  
                data: matchScoreObj
            }
        })); 

        // Let the ScoreZone Know = 1
        this.socket.send(JSON.stringify({
            type: "sendToGroup",
            noEcho: true,
            group: `${ adminID }-1`,
            data: {
                eventName: 'message', 
                action: 'update-score-basic',                  
                data: matchScoreObj
            }
        }));        

        return this.scoreTrackerBasicScoresSubject$;
    }  

    // ----------------------------------------
    // Send Scores From Score Tracker Round / ESPN
    // bagTracker Model with Inning Parms used by 
    // Charts
    // Currently tablets only join roomID / group 0
    // ----------------------------------------
    public emitUpdateScoreRound(matchScoreObj): Observable<any> 
    {
        console.log('Websocket >> update-score-round : ', matchScoreObj);

        // this.socket.send(JSON.stringify({'action': 'update-score-round', 'data': matchScoreObj}));
        let adminID = matchScoreObj.adminID;
        let eventID = matchScoreObj.leagueID;

        // Add the Team names to the save inning for cache purposes
        if(matchScoreObj.bracketType == 'T')
        {
            matchScoreObj['homeTeamName'] = matchScoreObj.homeTeam[0]?.poolTeamName;
            matchScoreObj['awayTeamName'] = matchScoreObj.awayTeam[0]?.poolTeamName;
        }        

        // Let the Leaderboard Know
        this.socket.send(JSON.stringify({
            type: "sendToGroup",
            noEcho: true,
            group: `${ adminID }-${ eventID }`,
            data: {
                eventName: 'message', 
                action: 'update-score-round',                  
                data: matchScoreObj
            }
        })); 

        // Let the ScoreZone Know = 1
        this.socket.send(JSON.stringify({
            type: "sendToGroup",
            noEcho: true,
            group: `${ adminID }-1`,
            data: {
                eventName: 'message', 
                action: 'update-score-round',                  
                data: matchScoreObj
            }
        }));         
        
        return this.scoreTrackerBasicScoresSubject$;

    }      

    // ----------------------------------------
    // Emit Event that Saved Overall Match Scores
    // was successfully from Device
    // ----------------------------------------
    public emitSavedMatchScoresBasicFromDevice(matchScoreObj): void 
    {
        console.log('Websocket >> saved-match-scores-basic-device : ', matchScoreObj);
        try 
        {
            // this.socket.send(JSON.stringify({'action': 'saved-match-scores-basic-device', 'data': matchScoreObj}));            
            let adminID = matchScoreObj.adminID;
            let eventID = matchScoreObj.leagueID;
    
            // Let the Leaderboard Know
            this.socket.send(JSON.stringify({
                type: "sendToGroup",
                noEcho: true,
                group: `${ adminID }-${ eventID }`,
                data: {
                    eventName: 'message', 
                    action: 'saved-match-scores-basic-device',                  
                    data: matchScoreObj
                }
            })); 
    
            // Let the ScoreZone Know = 1
            this.socket.send(JSON.stringify({
                type: "sendToGroup",
                noEcho: true,
                group: `${ adminID }-1`,
                data: {
                    eventName: 'message', 
                    action: 'saved-match-scores-basic-device',                  
                    data: matchScoreObj
                }
            }));                    

        } 
        catch (error) 
        {
            console.log('Socket is currently down');
            return;
        }
    }   


    // ----------------------------------------
    // Emit Event that Saved Overall Match Scores
    // was successfully from ADMIN
    // ----------------------------------------
    public emitSavedMatchScoresBasicFromAdmin(matchScoreObj): void 
    {
        console.log('Websocket >> saved-match-scores-basic-admin : ', matchScoreObj);
        // this.socket.send(JSON.stringify({'action': 'saved-match-scores-basic-admin', 'data': matchScoreObj}));
        let adminID = matchScoreObj.adminID;
        let eventID = matchScoreObj.leagueID;

        // Let the Leaderboard Know
        this.socket.send(JSON.stringify({
            type: "sendToGroup",
            noEcho: true,
            group: `${ adminID }-${ eventID }`,
            data: {
                eventName: 'message', 
                action: 'saved-match-scores-basic-admin',                  
                data: matchScoreObj
            }
        })); 

        // Let the ScoreZone Know = 1
        this.socket.send(JSON.stringify({
            type: "sendToGroup",
            noEcho: true,
            group: `${ adminID }-1`,
            data: {
                eventName: 'message', 
                action: 'saved-match-scores-basic-admin',                  
                data: matchScoreObj
            }
        }));           

    } 


    // ---------------------------------------------------
    // Button Functions
    // ---------------------------------------------------


    // Local Storage Used on the Device Setup Page
    // This ID will be used to send the Court Assignment Notifications to the correct device
    // ---------------------------------------------
    // SET LocalStorage Device Info
    // ---------------------------------------------
    public setLocalStorageDeviceInfo(uniqueDeviceInfo)
    {
        // set in any of your ts file
        localStorage.setItem(`socketIOUniqueDeviceInfo`, `${ JSON.stringify(uniqueDeviceInfo) }`);
        // console.log({status: 'OK', message: 'Successfully Create Device in Local Storage'});
        this.snackBar.open('Successfully Create Device in Local Storage', null, { duration: 1000 });
        // alert('Successfully Create Device in Local Storage');
        return;  
    }

    // ---------------------------------------------
    // Get LocalStorage Device Info
    // ---------------------------------------------
    public getLocalStorageDeviceInfo()
    {
        let deviceInfo = this.localStorage.getItem(`socketIOUniqueDeviceInfo`);
        return JSON.parse(deviceInfo);
    }

    // ---------------------------------------------
    // Get LocalStorage Device Info
    // ---------------------------------------------
    public removeLocalStorageDeviceInfo()
    {
        
        localStorage.removeItem(`socketIOUniqueDeviceInfo`);           
    }    


    resetAdminSave()
    {
        this.savedMatchScoresBasicFromAdminSubject$.next({eventID: 0});
    }
    

    getLatestAssignedMatchInfo()
    {
        // ------------------------------------------------------------
        // Get the logged in user id from the local token
        // ------------------------------------------------------------
        this.userTokenInfo = this.authService.getUserTokenInfo();

        if(this.localUniqueDeviceInfo == undefined)
        {
            this.checkDeviceSetup();
        }
        let url = `${ this.apiUrl }/api/v1/get-device-match-info/${ this.userTokenInfo.userID }?courtID=${ this.localUniqueDeviceInfo.deviceCourtNumber }`;

        return this.http.get(url);
    }      


    // On Reconnect Check the server for 
    checkDeviceCourtInfo()
    {
        this.getLatestAssignedMatchInfo().subscribe(
        (data: any) => 
        {
            // If the device is NOT already on the score tracker navigate
            // to the score tracker 
            // FIXME: add check for being on the practice mode and do not navigate away
            if(!window.location.pathname.includes('score-tracker'))
            {
                this.router.navigate([`score-tracker/${ data.data.eventID }`],
                {
                    queryParams: 
                    {   
                        leagueID:  data.data.eventID,
                        matchID: data.data.matchID,
                        gameID: 1, // data.data.currentGameID

                    }                    
                });  
            }
            
        },
        (error) => 
        {
            alert(error.message);
        });        

    }    

    // ------------------------------------
    // If you refresh on the scoreboard page 
    // need to reconnect the websocket and join the base group
    // and the event group
    // ------------------------------------
    joinEventGroup(eventID): Observable<any>
    {
        if(this.socket == null || this.socket?.OPEN != 1)
        {
            console.log('Websocket NOT CONNECTED');
            // return of({});
            return of({});
        }

        if(eventID == null)
        {
            return of({});
        }


        let tempWebSocketInfo = JSON.parse(this.localStorage.getItem(`webSocketInfo`));

        // Its Event is already in list do not rejoin
        if(this.checkGroupList(tempWebSocketInfo.groupsList, eventID))
        {
            return of({});
        }

        let param: any = {
            hubName: 'scorezone',
            connections: tempWebSocketInfo.connectionId,
            targetName: `${ this.connectionInfo.adminID }-${ eventID }`
        };
        let url = `${ this.socketHttpApiUrl }/grant-permission`;

        return this.http.put(url, {},  {params: param}).pipe(
            tap((res) =>
            {
                tempWebSocketInfo.groupsList = [...tempWebSocketInfo.groupsList, `${ this.connectionInfo.adminID }-${ eventID }`];
                localStorage.setItem(`webSocketInfo`, `${ JSON.stringify(tempWebSocketInfo) }`);
                // Set Local Storage
                console.log(`tap -> joinEventGroup() -> ${ this.connectionInfo.adminID }-${ eventID } `);
            },
            map((data) => data))
        );
    }      

    checkGroupList(groupsList, eventID)
    {
        if (groupsList.indexOf(`${ this.connectionInfo.adminID }-${ eventID }`) === -1)
        {
            return false;
        }
        else 
        {
            return true;
        }
    }
 
}
